import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Shield, CheckCircle2, XCircle, Clock, FileWarning } from "lucide-react";

const STATUS_BG = {
    review:   "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-blue-50 text-blue-700 border-blue-200",
    paid:     "bg-emerald-50 text-emerald-700 border-emerald-200",
    declined: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function AdminInsurance() {
    const [tab, setTab] = useState("review");
    const [claims, setClaims] = useState([]);
    const [decideOpen, setDecideOpen] = useState(false);
    const [active, setActive] = useState(null);
    const [decision, setDecision] = useState("approved");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    const load = (status) => api.get(`/insurance/admin/claims${status === "all" ? "" : `?status=${status}`}`).then((r) => setClaims(r.data));
    useEffect(() => { load(tab); }, [tab]);

    const decide = async () => {
        setBusy(true);
        try {
            await api.post(`/insurance/admin/claims/${active.id}/decide`, { decision, note });
            toast.success(`Claim ${decision}`);
            setDecideOpen(false);
            setNote("");
            load(tab);
        } catch (err) { toast.error("Failed"); }
        finally { setBusy(false); }
    };

    const openDecide = (claim, d) => {
        setActive(claim);
        setDecision(d);
        setDecideOpen(true);
    };

    return (
        <div data-testid="admin-insurance-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="font-display text-2xl font-semibold text-[#0A1930] flex items-center gap-2">
                        <Shield size={22} className="text-emerald-600" /> Insurance Claims
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">{claims.length} claims · review and decide payouts</p>
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mb-4">
                <TabsList className="bg-white border border-slate-200 rounded-xl p-1" data-testid="claims-filter">
                    <TabsTrigger value="review">In review</TabsTrigger>
                    <TabsTrigger value="paid">Paid</TabsTrigger>
                    <TabsTrigger value="declined">Declined</TabsTrigger>
                    <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
            </Tabs>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden product-shadow">
                <div className="overflow-x-auto admin-scroll">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left text-xs uppercase tracking-wider font-bold text-slate-500">
                                <th className="px-5 py-3">Claimant</th>
                                <th className="px-5 py-3">Product</th>
                                <th className="px-5 py-3">Reason</th>
                                <th className="px-5 py-3">Amount</th>
                                <th className="px-5 py-3">Filed</th>
                                <th className="px-5 py-3">Status</th>
                                <th className="px-5 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {claims.length === 0 && (
                                <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-slate-400">No claims in this bucket</td></tr>
                            )}
                            {claims.map((c) => (
                                <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`claim-row-${c.id}`}>
                                    <td className="px-5 py-3 font-medium">
                                        <div>{c.user_name}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{c.user_email}</div>
                                    </td>
                                    <td className="px-5 py-3"><Badge variant="outline" className="capitalize">{(c.policy_product || "").replace("_", " ")}</Badge></td>
                                    <td className="px-5 py-3 text-slate-700 max-w-[300px] truncate" title={c.reason}>{c.reason}</td>
                                    <td className="px-5 py-3 font-mono font-bold">{fmtCurrency(c.amount_requested)}</td>
                                    <td className="px-5 py-3 text-xs text-slate-500">{fmtDate(c.created_at)}</td>
                                    <td className="px-5 py-3"><Badge className={`capitalize border ${STATUS_BG[c.status]}`}>{c.status}</Badge></td>
                                    <td className="px-5 py-3">
                                        {c.status === "review" ? (
                                            <div className="flex gap-1">
                                                <Button onClick={() => openDecide(c, "approved")} size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${c.id}`}>
                                                    <CheckCircle2 size={12} /> Approve
                                                </Button>
                                                <Button onClick={() => openDecide(c, "declined")} size="sm" variant="outline" className="h-7 text-xs border-rose-200 text-rose-600 hover:bg-rose-50" data-testid={`decline-${c.id}`}>
                                                    <XCircle size={12} /> Decline
                                                </Button>
                                            </div>
                                        ) : (
                                            <span className="text-xs text-slate-400">{c.decision_by || "—"}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={decideOpen} onOpenChange={(v) => !v && setDecideOpen(false)}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            {decision === "approved" ? <CheckCircle2 size={18} className="text-emerald-600" /> : <XCircle size={18} className="text-rose-600" />}
                            {decision === "approved" ? "Approve & pay claim" : "Decline claim"}
                        </DialogTitle>
                        <DialogDescription>
                            {decision === "approved"
                                ? "Approving will instantly credit the user's wallet with the claimed amount. The claim moves to PAID."
                                : "Tell the user why this claim was declined. They'll see your note in their app."}
                        </DialogDescription>
                    </DialogHeader>
                    {active && (
                        <div className="space-y-3">
                            <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
                                <div className="flex justify-between"><span className="text-slate-500">Claimant</span><span className="font-semibold">{active.user_name}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Amount</span><span className="font-bold font-mono">{fmtCurrency(active.amount_requested)}</span></div>
                                <div className="flex justify-between"><span className="text-slate-500">Reason</span><span className="font-semibold text-right truncate max-w-[60%]">{active.reason}</span></div>
                            </div>
                            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder={decision === "approved" ? "Optional approval note…" : "Required: reason for decline"} className="rounded-xl" data-testid="decide-note-input" />
                            <Button onClick={decide} disabled={busy || (decision === "declined" && !note)} className={`w-full h-11 rounded-xl ${decision === "approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="decide-submit-btn">
                                {busy ? "Processing…" : decision === "approved" ? "Approve & pay out" : "Decline claim"}
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
